export function formatDate(date: string | Date | undefined): string {
  if (!date) return '';

  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('he-IL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatTimeAgo(createdAt: string, updatedAt?: string | null): string {
  const createdAtDate = new Date(createdAt);
  const updatedAtDate = updatedAt ? new Date(updatedAt) : null;
  const today = new Date();
  const timeDiffMinutes = Math.floor((today.getTime() - createdAtDate.getTime()) / (1000 * 60));
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const timeString = createdAtDate.toLocaleTimeString('he-IL', {
    hour: '2-digit',
    minute: '2-digit'
  });

  let timeText = '';
  if (timeDiffMinutes < 2 && timeDiffMinutes >= 0) {
    timeText = 'ממש עכשיו';
  } else if (timeDiffMinutes < 60 && timeDiffMinutes >= 0) {
    timeText = `לפני ${timeDiffMinutes} דקות`;
  } else if (createdAtDate.toDateString() === today.toDateString()) {
    timeText = `היום בשעה ${timeString}`;
  } else if (createdAtDate.toDateString() === yesterday.toDateString()) {
    timeText = `אתמול בשעה ${timeString}`;
  } else {
    timeText = `${createdAtDate.toLocaleDateString('he-IL')} בשעה ${timeString}`;
  }

  const wasEdited = updatedAtDate && updatedAtDate.getTime() !== createdAtDate.getTime();

  return `${timeText} ${wasEdited ? '(עודכן)' : ''}`;
}

export { parseDateValue, combineDateAndTime, resolveEventDateTime } from '@sm-dashboard/formatting';

export const calculateCountdown = (
  targetDate: Date | string | null | undefined,
  prefix = '',
  nowInput: Date | number = new Date()
): string | null => {
  if (!targetDate) return null;
  const expiry = targetDate instanceof Date ? targetDate : new Date(targetDate);
  if (Number.isNaN(expiry.getTime())) return null;

  const now = nowInput instanceof Date ? nowInput : new Date(nowInput);
  if (expiry.getTime() <= now.getTime()) return null;

  const diffMs = expiry.getTime() - now.getTime();
  const oneHourMs = 1000 * 60 * 60;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  const withPrefix = (text: string) => (prefix ? `${prefix} ${text}` : text);

  if (diffMs < oneHourMs) {
    const totalSeconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const compact = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    return withPrefix(compact);
  }

  if (diffDays > 0) {
    return withPrefix(`${diffDays} ימים ${diffHours} שעות ${diffMinutes} דקות`);
  }
  if (diffHours > 0) {
    return withPrefix(`${diffHours} שעות ${diffMinutes} דקות`);
  }
  return withPrefix(`${diffMinutes} דקות`);
};

export const parseExpiryDate = (targetDate: Date | string | null | undefined): Date | null => {
  if (!targetDate) return null;
  const expiry = targetDate instanceof Date ? targetDate : new Date(targetDate);
  return Number.isNaN(expiry.getTime()) ? null : expiry;
};

export const isExpiryDatePassed = (
  targetDate: Date | string | null | undefined,
  nowInput: Date | number = new Date()
): boolean => {
  const expiry = parseExpiryDate(targetDate);
  if (!expiry) return false;
  const now = nowInput instanceof Date ? nowInput : new Date(nowInput);
  return expiry.getTime() <= now.getTime();
};

export const isCountdownUnderOneHour = (targetDate: Date | string | null | undefined): boolean => {
  const expiry = parseExpiryDate(targetDate);
  if (!expiry) return false;
  const diffMs = expiry.getTime() - Date.now();
  return diffMs > 0 && diffMs < 1000 * 60 * 60;
};

export type CountdownCardState = { status: 'running'; text: string } | { status: 'closed' };

export const getCountdownCardState = (
  targetDate: Date | string | null | undefined,
  nowInput: Date | number = new Date()
): CountdownCardState | null => {
  const expiry = parseExpiryDate(targetDate);
  if (!expiry) return null;
  if (isExpiryDatePassed(expiry, nowInput)) {
    return { status: 'closed' };
  }
  const text = calculateCountdown(expiry, '', nowInput);
  if (!text) {
    return { status: 'closed' };
  }
  return { status: 'running', text };
};

/** Hebrew relative date, e.g. "היום", "אתמול", "לפני 3 ימים", "לפני חודשיים". */
export const formatRelativeDate = (date: string | Date | undefined, nowInput: Date = new Date()): string => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';

  const days = Math.round((d.getTime() - nowInput.getTime()) / (1000 * 60 * 60 * 24));
  const rtf = new Intl.RelativeTimeFormat('he', { numeric: 'auto' });

  if (Math.abs(days) < 7) return rtf.format(days, 'day');
  if (Math.abs(days) < 30) return rtf.format(Math.round(days / 7), 'week');
  if (Math.abs(days) < 365) return rtf.format(Math.round(days / 30), 'month');
  return rtf.format(Math.round(days / 365), 'year');
};
